<template>
  <v-dialog v-model="visible" persistent max-width="520px" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card class="d-flex flex-column">
      <div class="dialog-head" :class="{ 'dialog-head--warning': setup.tone === 'warning' || reverting }">
        <div>
          <div class="dialog-head-title">{{ reverting ? "Revert application" : setup.title || "Forward application" }}</div>
          <div class="dialog-head-subtitle">
            <template v-if="application">
              {{ application.applicationNo || application.applicationId || application._id }}
              <template v-if="application.campid && application.campid.campName"> · {{ application.campid.campName }}</template>
            </template>
          </div>
        </div>
        <v-btn icon dark @click="visible = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pt-6 flex-grow-1">
        <label class="field-label">
          {{ usernames.length > 1 ? "Send to" : "Sending to" }}
          <span v-if="usernames.length > 1" class="req">*</span>
        </label>

        <div v-if="loadingTargets" class="target-state">
          <v-progress-circular indeterminate size="18" width="2" color="#13736f"></v-progress-circular>
          Finding who this goes to…
        </div>

        <div v-else-if="!usernames.length" class="target-state target-state--empty">
          <v-icon small color="#b45309">mdi-account-alert-outline</v-icon>
          No officer is set up to receive this application yet.
        </div>

        <!-- One destination: no choice to make -->
        <div v-else-if="usernames.length === 1" class="target target--fixed">
          <span class="target-icon"><v-icon small color="#13736f">{{ targetIcon(usernames[0]) }}</v-icon></span>
          <span class="target-name">{{ usernames[0].name }}</span>
          <v-icon small color="#13736f">mdi-check-circle</v-icon>
        </div>

        <!-- Several destinations: show them all as options -->
        <div v-else class="target-options" role="radiogroup" aria-label="Send to">
          <button
            v-for="user in usernames"
            :key="user.userId"
            type="button"
            role="radio"
            :aria-checked="selectedUsername === user.name ? 'true' : 'false'"
            class="target"
            :class="{ 'target--active': selectedUsername === user.name }"
            @click="selectedUsername = user.name"
          >
            <span class="target-icon"><v-icon small :color="selectedUsername === user.name ? 'white' : '#13736f'">{{ targetIcon(user) }}</v-icon></span>
            <span class="target-name">{{ user.revert ? "Revert to " : "Forward to " }}{{ user.name }}</span>
            <v-icon small :color="selectedUsername === user.name ? '#13736f' : '#cbd5e1'">
              {{ selectedUsername === user.name ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}
            </v-icon>
          </button>
        </div>
        <div class="mb-5"></div>

        <template v-if="showAmount">
          <label class="field-label">
            Amount (₹) <span v-if="setup.amountFields === 'strict'" class="req">*</span>
          </label>
          <v-text-field
            v-model="amount"
            type="number"
            outlined
            dense
            hide-details
            color="#13736f"
            placeholder="Enter amount"
            prepend-inner-icon="mdi-currency-inr"
            class="mb-5"
          ></v-text-field>

          <label class="field-label">
            Amount breakdown <span v-if="setup.amountFields === 'strict'" class="req">*</span>
          </label>
          <v-textarea
            v-model="breakdownSummary"
            rows="3"
            auto-grow
            outlined
            dense
            hide-details
            color="#13736f"
            placeholder="How the amount is calculated"
            class="mb-5"
          ></v-textarea>
        </template>

        <label class="field-label">
          Remarks <span v-if="setup.requireReason !== false" class="req">*</span>
        </label>
        <v-textarea
          v-model="reason"
          rows="3"
          auto-grow
          outlined
          dense
          hide-details
          color="#13736f"
          placeholder="Reason / remarks"
        ></v-textarea>
      </v-card-text>

      <v-card-actions class="px-6 pb-5">
        <v-spacer></v-spacer>
        <v-btn depressed class="btn-act btn-act--neutral" @click="visible = false">Cancel</v-btn>
        <v-btn
          depressed
          class="btn-act px-6"
          :class="setup.tone === 'warning' || reverting ? 'btn-act--amber' : 'btn-act--primary'"
          :loading="submitting"
          @click="submit"
        >
          <v-icon small left>{{ reverting ? "mdi-undo-variant" : setup.confirmIcon || "mdi-send-outline" }}</v-icon>
          {{ reverting ? "Revert" : setup.confirmLabel || "Forward" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import { loadForwardTargets } from "@/components/Common/forwardTargets";


/*
 * Shared "forward / return application" dialog.
 *
 * setup (from the list or detail config):
 *   listUrl + targets [{ key, label, role }]  officers returned by listUrl
 *   cwwRole                                     use the CWW forward list instead
 *   includeApplicant, preselectFirst
 *   amountFields: "strict" | "optional" | "none" (paid camps only)
 *   requireReason (default true), title, confirmLabel, confirmIcon, tone
 *
 * target: optional destination already chosen by the caller (a
 *   "Forward to X" button or menu item); the dialog then skips the lookup.
 */
export default {
  name: "ForwardDialog",
  props: {
    value: { type: Boolean, default: false },
    application: { type: Object, default: null },
    setup: { type: Object, required: true },
    target: { type: Object, default: null },
  },
  data() {
    return {
      usernames: [],
      selectedUsername: null,
      loadingTargets: false,
      amount: "",
      breakdownSummary: "",
      reason: "",
      submitting: false,
    };
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isPaid() {
      return !!(this.application && this.application.campid && this.application.campid.campType !== "free");
    },
    // The destination chosen (or the only one on offer) sends the application back
    reverting() {
      const chosen =
        this.usernames.find((user) => user.name === this.selectedUsername) ||
        (this.usernames.length === 1 ? this.usernames[0] : null);
      if (chosen) return !!chosen.revert;
      return this.usernames.length > 0 && this.usernames.every((user) => user.revert);
    },
    showAmount() {
      return this.isPaid && (this.setup.amountFields === "strict" || this.setup.amountFields === "optional");
    },
  },
  watch: {
    value(open) {
      if (open) this.reset();
    },
  },
  methods: {
    targetIcon(user) {
      if (user.revert) return "mdi-undo-variant";
      return user.role === "User" ? "mdi-account-outline" : "mdi-briefcase-account-outline";
    },
    notify(message) {
      this.$emit("notify", message);
    },
    reset() {
      const app = this.application || {};
      this.amount = app.amount || "";
      this.breakdownSummary = app.amountBreakdown || "";
      this.reason = "";
      this.selectedUsername = null;
      this.usernames = [];
      if (app._id) this.loadTargets(app._id);
    },
    loadTargets(appid) {
      if (this.target) {
        this.usernames = [this.target];
        this.selectedUsername = this.target.name;
        return;
      }
      this.loadingTargets = true;
      loadForwardTargets(this.setup, appid)
        .then((users) => {
          this.usernames = users;
          // A single destination needs no choice; pick it.
          if (users.length === 1 || (this.setup.preselectFirst && users.length)) {
            this.selectedUsername = users[0].name;
          }
          this.loadingTargets = false;
        })
        .catch((err) => {
          this.loadingTargets = false;
          this.notify("Couldn't load the officers to forward to.");
          console.log(err);
        });
    },
    submit() {
      const setup = this.setup;
      if (!this.selectedUsername) {
        this.notify("Please select a user to forward to.");
        return;
      }
      const selectedUser = this.usernames.find((user) => user.name === this.selectedUsername);
      if (!selectedUser) return;

      if (setup.amountFields === "strict" && this.isPaid) {
        if (this.amount === "" || isNaN(parseFloat(this.amount))) {
          this.notify("Please enter a valid amount.");
          return;
        }
        if (!this.breakdownSummary || !this.breakdownSummary.trim()) {
          this.notify("Please enter amount breakdown summary.");
          return;
        }
      }
      if (setup.requireReason !== false && (!this.reason || !this.reason.trim())) {
        this.notify("Please enter remarks / reason.");
        return;
      }

      const data = {
        appid: this.application._id,
        forwardto: selectedUser.userId,
        reason: this.reason,
      };
      if (setup.amountFields === "strict") {
        // Section clerk / superintendent always send the amount fields.
        data.amount = this.isPaid ? this.amount : 0;
        data.amountBreakdown = this.isPaid ? this.breakdownSummary : "Free Camp";
      } else if (setup.amountFields === "optional" && this.isPaid) {
        data.amount = parseFloat(this.amount) || 0;
        data.amountBreakdown = this.breakdownSummary || "";
      }

      this.submitting = true;
      axios({
        method: "post",
        url: "/forward/",
        headers: {
          token: localStorage.getItem("token"),
        },
        data,
      })
        .then((response) => {
          this.submitting = false;
          this.notify(response.data.msg);
          if (response.data.status == true) {
            this.visible = false;
            this.$emit("done", response.data);
          }
        })
        .catch((err) => {
          this.submitting = false;
          this.notify("Couldn't forward the application. Please try again.");
          console.error(err);
        });
    },
  },
};
</script>

<style scoped>
.field-label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #475569;
}

.target-state {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafb;
  border: 1px dashed #d5e0e3;
  font-size: 14px;
  color: #64748b;
}

.target-state--empty {
  background: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
}

.target-options {
  display: grid;
  gap: 8px;
}

.target {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid #d5e0e3;
  background: #ffffff;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.target:hover {
  border-color: rgba(19, 115, 111, 0.5);
}

.target:focus-visible {
  outline: 2px solid #13736f;
  outline-offset: 2px;
}

.target--active {
  border-color: #13736f;
  background: rgba(19, 115, 111, 0.06);
}

.target--fixed {
  cursor: default;
  border-color: rgba(19, 115, 111, 0.35);
  background: rgba(19, 115, 111, 0.06);
}

.target-icon {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(19, 115, 111, 0.1);
}

.target--active .target-icon {
  background: #13736f;
}

.target-name {
  flex: 1;
  min-width: 0;
  font-size: 14.5px;
  font-weight: 700;
  color: #0f172a;
  word-break: break-word;
}

.req {
  color: #dc2626;
}

.dialog-head--warning {
  background: linear-gradient(120deg, #92400e 0%, #d97706 100%);
}
</style>
